import { SubtractiveGenerator, GameLogic, ScoreData, ActionBus, Dealer } from 'game-logic';
import { DataImporter } from './data/data-importer';
import { Cell } from './grid/cell';
import { CellPoints } from './grid/cell-points';
import { Grid } from './grid/grid';
import { Block } from './models/block';
import { BlockGridProjection } from './models/block-grid-projection';
import { GridPosition } from './models/grid-position';
import { Move } from './models/move';
import { Shape } from './models/shape';
import { ShapeProjection } from './models/shape-projection';
import { ShapeBoard } from './shape-board';
import { MoveListSerializer } from './move-list-serializer';
import { GridElement } from './grid/grid-element';
import { delay } from 'src/games/common/helper';
import { GameMetricsType, getGameMetrics } from './helpers/game-metrics';

export type BlocksLogicAction = {
  onGameLost: void;
  onGameAutoComplete: void;
  onGameCompleted: void;
  onMoveExecuted: { moves: string[]; existingDeal: boolean };
  onFirstShapePlaced: void;
  onNextStage: { level: number };
  onNextThreeShapes: void;
  onShapeRemoved: { shapeId: string };
  onMultiplierChanged: { value: number };
  onLevelChanged: { value: number };
  onScoreChanged: { value: number };
  onAfterShapePlaced: void;
  onWinCellCollected: { cell: Cell; points: number; displayIndex: number };
  onRemainingShapesCanBePlaced: { validShapes: Shape[] };
  onShapePointsAdded: {
    points: number;
    shape: Shape;
    cellId: string;
    gridElementsToCollect: GridElement[];
  };
};

export class BlocksGameLogic implements GameLogic {
  public get gameLogicActions() {
    return this.logicActionBus;
  }
  public get score() {
    return this._score;
  }
  public get currentShapes() {
    return this.shapeBoard.currentShapes;
  }
  public get currentGrid() {
    return this.grid;
  }
  public movesList: Move[] = [];

  private gameMetrics: GameMetricsType;
  private grid: Grid;
  private virtualGrid: Grid;
  private shapeBoard: ShapeBoard;
  private cellPoints: CellPoints[];
  private highlightedCells: Cell[];
  private highlightedBlocks: Block[];
  private cellsToRemove: Cell[];
  private logicActionBus: ActionBus<BlocksLogicAction>;

  private _score;
  private _multiplier;
  private _currentStage;
  private _isFirstShapePlaced;
  private _disclosedSecretsCount;
  private _instantFinishScaler = 1.0;
  private _runningDealerTasks: Promise<void>[] = [];

  constructor(
    private dealer: Dealer<number>,
    readonly logicVersion: number,
  ) {
    this.gameMetrics = getGameMetrics(logicVersion);
    this.grid = new Grid(this.gameMetrics);
    this.virtualGrid = new Grid(this.gameMetrics);
    this.shapeBoard = new ShapeBoard(this.gameMetrics);
    this.cellPoints = DataImporter.importCellPoints();
    this.highlightedCells = [];
    this.highlightedBlocks = [];
    this.cellsToRemove = [];
    this.logicActionBus = new ActionBus<BlocksLogicAction>();

    this._score = 0;
    this._multiplier = 1;
    this._currentStage = 1;
    this._isFirstShapePlaced = false;
    this._disclosedSecretsCount = 0;
  }

  serializeCommandList(): string[] {
    return this.movesList.map((move) => move.serializeToString());
  }

  generateScoreData(): ScoreData {
    return {
      score: this._score,
    };
  }

  public setInstantFinish() {
    this._instantFinishScaler = 0.05;
  }

  public cellsToProjectIfLastMove(): Cell[] {
    const projectionsList: ShapeProjection[] = [];

    for (const shape of this.currentShapes) {
      for (const cell of this.grid.cells) {
        const projection = this.projectShapeOnGrid(shape, cell.id, this.grid);

        if (projection.length > 0) {
          projectionsList.push({
            shape,
            blockProjections: projection,
          });
          break;
        }
      }

      // If we've already determined multiple projections, no need to proceed further
      if (projectionsList.length > 1) {
        return [];
      }
    }

    // If we found only one valid projection
    if (projectionsList.length === 1) {
      const { shape } = projectionsList[0];
      const shapeProjections: ShapeProjection[] = [];

      for (const cell of this.grid.cells) {
        const blockProjections = this.projectShapeOnGrid(shape, cell.id, this.grid);

        if (blockProjections.length > 0) {
          shapeProjections.push({
            shape,
            blockProjections,
          });

          if (shapeProjections.length > 1) {
            return [];
          }
        }
      }

      // Return the cells if only one projection remains
      if (shapeProjections.length === 1) {
        return shapeProjections[0].blockProjections.map((projection) => projection.cell);
      }
    }

    return [];
  }

  public cellsToHighlight(shape: Shape, cellId: string): [Cell[], Block[]] {
    this.highlightedCells = [];
    this.highlightedBlocks = [];
    this.cellsToRemove = [];

    this.grid.cells.forEach((cell, index) => {
      const virtualCell = this.virtualGrid.cells.find((item) => item.id === cell.id);
      if (virtualCell) {
        virtualCell.setBlock({ block: cell.currentBlock, displayIndex: index });
        virtualCell.setPoints(cell.currentPoints.amount, cell.currentPoints.display);
      }
    });

    const projectionsList = this.projectShapeOnGrid(shape, cellId, this.virtualGrid);
    projectionsList.forEach((projection, index) => {
      projection.cell.setBlock({ block: projection.block, displayIndex: index });
    });

    const gridElementsToCollect = this.virtualGrid.gridElementsToCollect();
    gridElementsToCollect.forEach((gridElement) => {
      gridElement.cells.forEach((cell) => {
        this.highlightedCells.push(cell);
      });
    });

    this.highlightedCells.forEach((cell) => {
      projectionsList.forEach((projection) => {
        if (cell.id === projection.cell.id) {
          this.cellsToRemove.push(cell);
          this.highlightedBlocks.push(projection.block);
        }
      });
    });

    this.highlightedCells = this.highlightedCells.filter(
      (cell) => !this.cellsToRemove.includes(cell),
    );

    return [this.highlightedCells, this.highlightedBlocks];
  }

  public projectShapeOnGrid(shape: Shape, originCellId: string, grid: Grid): BlockGridProjection[] {
    const originCell = grid.cellById(originCellId);
    if (!originCell) {
      return [];
    }

    const originBlockPointOnGrid = originCell.positionOnGrid;

    const projectionsList: BlockGridProjection[] = [];
    for (const block of shape.blocks) {
      const targetPos = GridPosition.add(originBlockPointOnGrid, block.posInShape);
      const foundCell = grid.cellById(`${targetPos.row}${targetPos.col}`);
      if (!foundCell) {
        return [];
      }

      if (foundCell.currentBlock !== null) {
        return [];
      }

      projectionsList.push({
        cell: foundCell,
        block: block,
      });
    }

    return projectionsList;
  }

  public execute(move: Move, existingDeal: boolean) {
    const shape = this.shapeBoard.currentShapes.find((shape) => shape.shapeNum === move.shapeNum);
    if (shape) {
      this.placeShapeOnGrid(shape, move.cellId, existingDeal, false);
    }
  }

  public async placeShapeOnGrid(
    shape: Shape,
    cellId: string,
    existingDeal: boolean,
    awaitAnimation: boolean,
  ): Promise<void> {
    const move = new Move(shape.shapeNum, cellId);
    const projectionsList = this.projectShapeOnGrid(shape, cellId, this.grid);
    if (projectionsList.length === 0) {
      throw new Error(`Shape cannot be placed here: ${move.serializeToString()}`);
    }

    this.movesList.push(move);
    this.logicActionBus.emit('onMoveExecuted', {
      moves: [move.serializeToString()],
      existingDeal,
    });

    if (projectionsList.length > 0 && !this._isFirstShapePlaced) {
      this.logicActionBus.emit('onFirstShapePlaced');
      this._isFirstShapePlaced = true;
    }

    let cellsPoints = 0;

    projectionsList.forEach((projection, index) => {
      cellsPoints += projection.cell.currentPointsAmount;
      cellsPoints += this.gameMetrics.PointsPerBlock;
      projection.cell.setBlock({ block: projection.block, displayIndex: index });
    });

    this.collectGridElements(shape, cellId, cellsPoints);

    if (this.currentShapes.some((item) => item.shapeNum === shape.shapeNum)) {
      const index = this.currentShapes.findIndex((item) => item.shapeNum === shape.shapeNum);
      if (index !== -1) {
        // Remove the item at the found index
        this.currentShapes.splice(index, 1);

        // Notify out to delete the shape from the scene
        this.logicActionBus.emit('onShapeRemoved', { shapeId: shape.id });
      }
    }

    if (this.currentShapes.length === 0) {
      this._currentStage += 1;
      if (!this.isGameCompleted()) {
        this.logicActionBus.emit('onLevelChanged', { value: this._currentStage });
        this.nextRound();
      }
    } else {
      this.checkIfRemainingShapesCanBePlacedOnGrid();
    }

    this.logicActionBus.emit('onAfterShapePlaced');

    if (this.isGameCompleted()) {
      await this.collectAllBlocksAndNotifyAboutGameCompletion(awaitAnimation);
    }
  }

  private checkIfRemainingShapesCanBePlacedOnGrid() {
    const projectionsList: ShapeProjection[] = [];

    for (const shape of this.currentShapes) {
      for (const cell of this.grid.cells) {
        const projection = this.projectShapeOnGrid(shape, cell.id, this.grid);
        if (projection.length > 0) {
          projectionsList.push({
            shape,
            blockProjections: projection,
          });
          break;
        }
      }
    }

    this.logicActionBus.emit('onRemainingShapesCanBePlaced', {
      validShapes: projectionsList.map((projection) => projection.shape),
    });

    const canPlaceRemainingShapes = projectionsList.some(
      (item) => item.blockProjections.length > 0,
    );
    if (!canPlaceRemainingShapes) {
      this.loseGame();
    }
  }

  private loseGame() {
    this.logicActionBus.emit('onGameLost');
  }

  private isGameCompleted(): boolean {
    return this._currentStage === this.gameMetrics.MaxStage;
  }

  private async collectAllBlocksAndNotifyAboutGameCompletion(
    awaitAnimation: boolean,
  ): Promise<void> {
    const cellsWithBlocks = this.grid.cells.filter((cell) => cell.currentBlock !== null);

    this.logicActionBus.emit('onGameAutoComplete');

    let displayScore = this._score;
    for (const [index, cell] of cellsWithBlocks.entries()) {
      this.logicActionBus.emit('onWinCellCollected', {
        cell: cell,
        points: this.gameMetrics.PointsPerBlockOnWin,
        displayIndex: index,
      });
      displayScore += this.gameMetrics.PointsPerBlockOnWin;
      this.logicActionBus.emit('onScoreChanged', { value: displayScore });

      if (awaitAnimation) {
        await delay(150 * this._instantFinishScaler);
      }
    }

    const totalPoints = this.gameMetrics.PointsPerBlockOnWin * cellsWithBlocks.length;
    this.updatePoints(totalPoints, true, false);

    if (awaitAnimation) {
      await delay(1000 * this._instantFinishScaler);
    }

    this.logicActionBus.emit('onGameCompleted');
  }

  private collectGridElements(shape: Shape, cellId: string, cellsPoints: number) {
    let totalPoints = cellsPoints;
    const gridElementsToCollect = this.grid.gridElementsToCollect();

    totalPoints += this.gameMetrics.PointsPerGridElement * gridElementsToCollect.length;

    gridElementsToCollect.forEach((gridElement) => {
      gridElement.cells.forEach((cell) => {
        if (cell.blockMultiplier) {
          this.updateMultiplier(cell.blockMultiplier);
        }
      });
    });

    this.updatePoints(totalPoints, false, true);

    this.logicActionBus.emit('onShapePointsAdded', {
      points: totalPoints * this._multiplier,
      shape: shape,
      cellId: cellId,
      gridElementsToCollect: gridElementsToCollect,
    });

    gridElementsToCollect.forEach((gridElement) => {
      gridElement.setGridElementEmpty();
    });
  }

  private updatePoints(points: number, autoFinish: boolean, multiplier: boolean) {
    let totalPoints = points;
    if (multiplier) {
      totalPoints = points * this._multiplier;
    }

    this._score += totalPoints;
    this.logicActionBus.emit('onScoreChanged', { value: this._score });
  }

  private updateMultiplier(multiplier: number) {
    this._multiplier += multiplier;
    this.logicActionBus.emit('onMultiplierChanged', { value: this._multiplier });
  }

  public nextRound() {
    this._runningDealerTasks.push(
      this.askDealerForSeedAndStartNextRound(
        [this._currentStage - 1],
        MoveListSerializer.getHash(this.movesList),
      ),
    );

    this._disclosedSecretsCount += 1;
  }

  public async waitForAllTasksToBeCompleted(): Promise<void> {
    await Promise.all(this._runningDealerTasks);
    this._runningDealerTasks = [];
  }

  private async askDealerForSeedAndStartNextRound(indexes: number[], hash: string): Promise<void> {
    const seed = await this.dealer.discloseSecrets(indexes, hash);

    const random = new SubtractiveGenerator(seed.length >= 1 ? seed[0] : 0);

    this.shapeBoard.showNextThreeShapes(random, this._currentStage);
    this.spawnPointsOnCell(random, this.gameMetrics.SpawnPoints);
    this.logicActionBus.emit('onNextThreeShapes');
    this.logicActionBus.emit('onNextStage', { level: this._currentStage });

    this.checkIfRemainingShapesCanBePlacedOnGrid();
  }

  private spawnPointsOnCell(random: SubtractiveGenerator, points: number) {
    const targetCell = this.grid.getCellToAssignPoints(random);
    if (targetCell === null) {
      return;
    }

    if (targetCell.currentPointsAmount > 0) {
      const nextPointsIndex =
        this.cellPoints.findIndex((item) => item.points === targetCell.currentPointsAmount) + 1;
      let nextPoints = 0;
      let nextPointsDisplay = '0';

      if (nextPointsIndex < this.cellPoints.length) {
        nextPoints = this.cellPoints[nextPointsIndex].points;
        nextPointsDisplay = this.cellPoints[nextPointsIndex].pointsString;
      }

      // TODO: We should somehow display the number of points getting added to each cell
      // This is the diff
      // const pointsIncrement = nextPoints - targetCell.currentPointsAmount;

      targetCell.setPoints(nextPoints, nextPointsDisplay);
      return;
    }

    targetCell.setPoints(points, `${points}`);
  }
}
